/**
 * SourceDetailsPanel Component
 *
 * Expanded details panel for a source showing:
 * - Volume trend chart (30 days)
 * - Authentication breakdown (SPF/DKIM pass/fail with alignment distinction)
 * - IP and hostname details
 * - Mail From (envelope_from) domain
 * - Forwarder status
 *
 * For forwarders, DKIM alignment always fails because they modify messages.
 * We show DKIM auth rate instead with an explanatory tooltip.
 */

import { useState } from "react";
import { Loader2, TrendingUp, Shield, Server, Calendar, Mail, ArrowRightLeft, Info, Key, ChevronDown, ChevronUp, Table2 } from "lucide-react";
import { SourceMiniChart } from "./SourceMiniChart";
import type { Source, SourceDailyStat, SourceMetadata, DailyDetail } from "@/lib/api";

// Helper to check if a source is a forwarder
function isForwarder(source: Source): boolean {
  const forwarderTypes = ["forwarder", "mailing_list", "trusted_forwarder", "arc_forwarder"];
  if (source.source_type && forwarderTypes.includes(source.source_type)) {
    return true;
  }
  return source.forwarding_detected === true;
}

interface SourceDetailsPanelProps {
  source: Source;
  dailyStats: SourceDailyStat[] | null;
  loading: boolean;
  metadata?: SourceMetadata | null;
}

// Helper to format source type for display
function formatSourceType(sourceType: string): string {
  const typeNames: Record<string, string> = {
    forwarder: "Forwarder",
    mailing_list: "Mailing List",
    trusted_forwarder: "Trusted Forwarder",
    arc_forwarder: "ARC Forwarder",
    direct: "Direct",
    unknown: "Unknown",
  };
  return typeNames[sourceType] || sourceType;
}

function SelectorRow({ sel, dayTotal, rateColor, rateBg, highlight }: {
  sel: { selector: string; dkim_domain?: string | null; pass: number; fail: number; total: number };
  dayTotal: number;
  rateColor: (pass: number, total: number) => string;
  rateBg: (pass: number, total: number) => string;
  highlight?: boolean;
}) {
  const passRate = sel.total > 0 ? (sel.pass / sel.total) * 100 : 0;
  const barWidth = dayTotal > 0 ? Math.max((sel.total / dayTotal) * 100, 2) : 0;
  return (
    <div className={`flex items-center gap-3 py-1 ${highlight ? "bg-red-50/40 -mx-3 px-3 rounded" : ""}`}>
      <div className="min-w-[220px] flex items-center gap-1.5">
        <Key className="w-3 h-3 text-slate-300 flex-shrink-0" />
        <span className="font-mono text-xs text-slate-600 truncate" title={sel.selector}>
          {sel.selector}
        </span>
        {sel.dkim_domain && (
          <span className={`text-[10px] px-1 py-0.5 rounded truncate ${highlight ? "bg-red-100 text-red-600 font-medium" : "text-slate-400"}`} title={`Signing domain: ${sel.dkim_domain}`}>
            {sel.dkim_domain}
          </span>
        )}
      </div>
      <div className="flex-1 max-w-[180px] h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${highlight ? "bg-red-400" : passRate >= 80 ? "bg-emerald-400" : passRate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <span className="text-xs font-medium text-slate-700 min-w-[50px] text-right">
        {sel.total.toLocaleString()}
      </span>
      <span className={`text-xs font-semibold min-w-[45px] text-right px-1.5 py-0.5 rounded ${rateBg(sel.pass, sel.total)} ${rateColor(sel.pass, sel.total)}`}>
        {passRate.toFixed(0)}%
      </span>
      <span className="text-[10px] text-slate-400 min-w-[90px]">
        {sel.pass.toLocaleString()} pass / {sel.fail.toLocaleString()} fail
      </span>
    </div>
  );
}

export function DailyBreakdownTable({ dailyDetail, onExportCsv }: { dailyDetail: DailyDetail[]; onExportCsv?: () => void }) {
  const [showAll, setShowAll] = useState(false);
  const displayData = showAll ? dailyDetail : dailyDetail.slice(0, 7);

  if (dailyDetail.length === 0) return null;

  const rateColor = (pass: number, total: number) => {
    if (total === 0) return "text-slate-400";
    const rate = (pass / total) * 100;
    return rate >= 80 ? "text-emerald-700" : rate >= 50 ? "text-amber-600" : "text-red-600";
  };

  const rateBg = (pass: number, total: number) => {
    if (total === 0) return "bg-slate-100";
    const rate = (pass / total) * 100;
    return rate >= 80 ? "bg-emerald-50" : rate >= 50 ? "bg-amber-50" : "bg-red-50";
  };

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 text-slate-600">
          <Table2 className="w-4 h-4" />
          <span className="text-sm font-medium">Daily Breakdown</span>
          <span className="text-xs text-slate-400">({dailyDetail.length} days)</span>
        </div>
        <div className="flex items-center gap-3">
          {onExportCsv && (
            <button
              onClick={onExportCsv}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              Export CSV
            </button>
          )}
          {dailyDetail.length > 7 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
            >
              {showAll ? (
                <>Show less <ChevronUp className="w-3 h-3" /></>
              ) : (
                <>Show all {dailyDetail.length} days <ChevronDown className="w-3 h-3" /></>
              )}
            </button>
          )}
        </div>
      </div>
      <div className="divide-y divide-slate-200">
        {displayData.map((day) => {
          const spfTotal = day.spf_pass + day.spf_fail;
          const spfRate = spfTotal > 0 ? (day.spf_pass / spfTotal) * 100 : null;
          const dmarcTotal = (day.dmarc_pass ?? 0) + (day.dmarc_fail ?? 0);
          const dmarcRate = dmarcTotal > 0 ? ((day.dmarc_pass ?? 0) / dmarcTotal) * 100 : null;
          const dispNone = day.disposition_none ?? 0;
          const dispQuarantine = day.disposition_quarantine ?? 0;
          const dispReject = day.disposition_reject ?? 0;
          const headerFrom = (day.header_from ?? []);
          const envelopeFrom = (day.envelope_from ?? []);
          // Split selectors into aligned (signing domain matches header from) vs misaligned
          const headerFromSet = new Set(headerFrom.map(d => d.toLowerCase()));
          const isAligned = (sel: typeof day.selectors[0]) => {
            if (!sel.dkim_domain) return true; // no signing domain info = assume aligned
            return headerFromSet.has(sel.dkim_domain.toLowerCase());
          };
          const alignedSelectors = day.selectors.filter(isAligned).sort((a, b) => b.total - a.total);
          const misalignedSelectors = day.selectors.filter(s => !isAligned(s)).sort((a, b) => b.total - a.total);
          const alignedVolume = alignedSelectors.reduce((sum, s) => sum + s.total, 0);
          const misalignedVolume = misalignedSelectors.reduce((sum, s) => sum + s.total, 0);

          return (
            <div key={day.date}>
              {/* Date summary row */}
              <div className="px-4 py-2.5 bg-slate-50/60 flex flex-wrap items-center gap-x-5 gap-y-1">
                <span className="text-sm font-semibold text-slate-800 min-w-[70px]">
                  {new Date(day.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
                <span className="text-sm font-semibold text-slate-800">
                  {day.total_messages.toLocaleString()} <span className="font-normal text-slate-500">msgs</span>
                </span>
                {headerFrom.length > 0 && (
                  <span className="text-xs text-slate-500">
                    From: <span className="font-mono text-slate-700">{headerFrom.join(", ")}</span>
                  </span>
                )}
                {envelopeFrom.length > 0 && (
                  <span className="text-xs text-slate-500">
                    Env: <span className="font-mono text-blue-600">{envelopeFrom.join(", ")}</span>
                  </span>
                )}
                <div className="flex items-center gap-3 ml-auto">
                  <span className={`text-xs font-semibold ${spfRate !== null ? rateColor(day.spf_pass, spfTotal) : "text-slate-400"}`}>
                    SPF {spfRate !== null ? `${spfRate.toFixed(0)}%` : "—"}
                  </span>
                  <span className={`text-xs font-semibold ${dmarcRate !== null ? rateColor(day.dmarc_pass ?? 0, dmarcTotal) : "text-slate-400"}`}>
                    DMARC {dmarcRate !== null ? `${dmarcRate.toFixed(0)}%` : "—"}
                  </span>
                  <span className="text-xs text-slate-500">
                    {dispNone > 0 && <span className="text-emerald-700">{dispNone.toLocaleString()}</span>}
                    {dispQuarantine > 0 && <span className="text-amber-600 ml-1">Q:{dispQuarantine.toLocaleString()}</span>}
                    {dispReject > 0 && <span className="text-red-600 ml-1">R:{dispReject.toLocaleString()}</span>}
                  </span>
                </div>
              </div>
              {/* Selector sub-rows grouped by alignment */}
              {(alignedSelectors.length > 0 || misalignedSelectors.length > 0) && (
                <div className="px-4 py-1.5">
                  {/* Aligned selectors */}
                  {alignedSelectors.length > 0 && (
                    <div className="border-l-2 border-emerald-300 pl-3 py-1">
                      {(alignedSelectors.length > 0 && misalignedSelectors.length > 0) && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">Aligned</span>
                          <span className="text-[10px] text-slate-400">{alignedVolume.toLocaleString()} msgs</span>
                        </div>
                      )}
                      {alignedSelectors.map((sel, i) => (
                        <SelectorRow key={i} sel={sel} dayTotal={day.total_messages} rateColor={rateColor} rateBg={rateBg} />
                      ))}
                    </div>
                  )}
                  {/* Misaligned selectors */}
                  {misalignedSelectors.length > 0 && (
                    <div className="border-l-2 border-red-300 pl-3 py-1 mt-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-red-500">Not Aligned</span>
                        <span className="text-[10px] text-slate-400">{misalignedVolume.toLocaleString()} msgs — signing domain ≠ From:</span>
                      </div>
                      {misalignedSelectors.map((sel, i) => (
                        <SelectorRow key={i} sel={sel} dayTotal={day.total_messages} rateColor={rateColor} rateBg={rateBg} highlight />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Export daily detail data as CSV — one row per selector per date
export function exportDailyDetailCsv(dailyDetail: DailyDetail[], label: string) {
  const headers = [
    "Date",
    "Header From",
    "Envelope From",
    "Day Total Messages",
    "Day SPF Pass",
    "Day SPF Fail",
    "Day SPF Rate",
    "Day DMARC Pass",
    "Day DMARC Fail",
    "Day DMARC Rate",
    "Delivered",
    "Quarantined",
    "Rejected",
    "DKIM Selector",
    "Signing Domain",
    "Aligned",
    "Selector Messages",
    "Selector DKIM Pass",
    "Selector DKIM Fail",
    "Selector DKIM Rate",
  ];

  const rows: (string | number)[][] = [];

  for (const day of dailyDetail) {
    const spfTotal = day.spf_pass + day.spf_fail;
    const dmarcTotal = (day.dmarc_pass ?? 0) + (day.dmarc_fail ?? 0);
    const headerFrom = (day.header_from ?? []).join("; ");
    const envelopeFrom = (day.envelope_from ?? []).join("; ");
    const headerFromSet = new Set((day.header_from ?? []).map(d => d.toLowerCase()));

    const dateStr = day.date;
    const daySpfRate = spfTotal > 0 ? ((day.spf_pass / spfTotal) * 100).toFixed(1) + "%" : "";
    const dayDmarcRate = dmarcTotal > 0 ? (((day.dmarc_pass ?? 0) / dmarcTotal) * 100).toFixed(1) + "%" : "";

    const sortedSelectors = [...day.selectors].sort((a, b) => b.total - a.total);

    if (sortedSelectors.length === 0) {
      // Date row with no selector detail
      rows.push([
        dateStr, headerFrom, envelopeFrom,
        day.total_messages,
        day.spf_pass, day.spf_fail, daySpfRate,
        day.dmarc_pass ?? 0, day.dmarc_fail ?? 0, dayDmarcRate,
        day.disposition_none ?? 0, day.disposition_quarantine ?? 0, day.disposition_reject ?? 0,
        "", "", "", "", "", "", "",
      ]);
    } else {
      for (const sel of sortedSelectors) {
        const aligned = sel.dkim_domain
          ? headerFromSet.has(sel.dkim_domain.toLowerCase()) ? "Yes" : "No"
          : "";
        const selRate = sel.total > 0 ? ((sel.pass / sel.total) * 100).toFixed(1) + "%" : "";
        rows.push([
          dateStr, headerFrom, envelopeFrom,
          day.total_messages,
          day.spf_pass, day.spf_fail, daySpfRate,
          day.dmarc_pass ?? 0, day.dmarc_fail ?? 0, dayDmarcRate,
          day.disposition_none ?? 0, day.disposition_quarantine ?? 0, day.disposition_reject ?? 0,
          sel.selector, sel.dkim_domain ?? "", aligned,
          sel.total, sel.pass, sel.fail, selRate,
        ]);
      }
    }
  }

  const csvContent = [headers, ...rows].map((row) =>
    row.map((cell) => {
      const str = String(cell ?? "");
      return str.includes(",") || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(",")
  ).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `daily-breakdown-${label}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function SourceDetailsPanel({
  source,
  dailyStats,
  loading,
  metadata,
}: SourceDetailsPanelProps) {
  // Calculate aggregated stats from daily data OR fall back to source's aggregate data
  const hasDailyStats = dailyStats && dailyStats.length > 0 &&
    dailyStats.some(d => d.message_count > 0);

  const aggregatedStats = hasDailyStats
    ? dailyStats!.reduce(
        (acc, d) => ({
          totalMessages: acc.totalMessages + d.message_count,
          spfPass: acc.spfPass + d.spf_pass_count,
          spfFail: acc.spfFail + d.spf_fail_count,
          dkimPass: acc.dkimPass + d.dkim_pass_count,
          dkimFail: acc.dkimFail + d.dkim_fail_count,
          dmarcPass: acc.dmarcPass + d.dmarc_pass_count,
          dmarcFail: acc.dmarcFail + d.dmarc_fail_count,
        }),
        {
          totalMessages: 0,
          spfPass: 0,
          spfFail: 0,
          dkimPass: 0,
          dkimFail: 0,
          dmarcPass: 0,
          dmarcFail: 0,
        }
      )
    : source.total_message_count > 0
      ? (() => {
          // Fall back to source's aggregate data when daily stats aren't available
          const totalMessages = source.total_message_count;
          const spfRate = source.spf_pass_rate || 0;
          const dkimRate = source.dkim_pass_rate || 0;
          // Estimate pass/fail counts from rates
          const spfPass = Math.round(spfRate * totalMessages);
          const spfFail = Math.round((1 - spfRate) * totalMessages);
          const dkimPass = Math.round(dkimRate * totalMessages);
          const dkimFail = Math.round((1 - dkimRate) * totalMessages);
          // DMARC passes when either SPF OR DKIM alignment passes
          // Calculate using union probability: P(A ∪ B) = 1 - (1-P(A))*(1-P(B))
          const dmarcRate = 1 - (1 - spfRate) * (1 - dkimRate);
          const dmarcPass = Math.round(dmarcRate * totalMessages);
          const dmarcFail = totalMessages - dmarcPass;
          return {
            totalMessages,
            spfPass,
            spfFail,
            dkimPass,
            dkimFail,
            dmarcPass,
            dmarcFail,
          };
        })()
      : null;

  // Calculate pass rates from aggregated data
  const calculateRate = (pass: number, fail: number) => {
    const total = pass + fail;
    return total > 0 ? ((pass / total) * 100).toFixed(1) : "N/A";
  };

  if (loading) {
    return (
      <div className="py-6 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading statistics...
      </div>
    );
  }

  return (
    <div className="py-4 px-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Volume Trend */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-3">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">Volume {hasDailyStats ? "(30 days)" : "(Total)"}</span>
          </div>
          {hasDailyStats ? (
            <SourceMiniChart data={dailyStats || []} height={80} />
          ) : aggregatedStats ? (
            <div className="flex items-center justify-center h-20 text-sm text-slate-400">
              Daily breakdown not available
            </div>
          ) : (
            <SourceMiniChart data={[]} height={80} />
          )}
          {aggregatedStats && (
            <div className="mt-2 text-center">
              <span className="text-lg font-semibold text-slate-800">
                {aggregatedStats.totalMessages.toLocaleString()}
              </span>
              <span className="text-sm text-slate-500 ml-1">messages</span>
            </div>
          )}
        </div>

        {/* Authentication Breakdown */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-3">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Authentication (30 days)</span>
          </div>
          {aggregatedStats ? (
            <div className="space-y-3">
              {/* SPF Alignment */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 flex items-center gap-1">
                    SPF Aligned
                    <span className="text-xs text-slate-400" title="SPF passed AND domain aligns with From header">
                      <Info className="w-3 h-3" />
                    </span>
                  </span>
                  <span
                    className={`font-medium ${
                      parseFloat(
                        calculateRate(aggregatedStats.spfPass, aggregatedStats.spfFail)
                      ) >= 80
                        ? "text-emerald-700"
                        : parseFloat(
                            calculateRate(aggregatedStats.spfPass, aggregatedStats.spfFail)
                          ) >= 50
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  >
                    {calculateRate(aggregatedStats.spfPass, aggregatedStats.spfFail)}%
                  </span>
                </div>
                <div className="flex gap-1 text-xs text-slate-500">
                  <span className="text-emerald-700">
                    {aggregatedStats.spfPass.toLocaleString()} aligned
                  </span>
                  <span>/</span>
                  <span className="text-red-600">
                    {aggregatedStats.spfFail.toLocaleString()} not aligned
                  </span>
                </div>
              </div>

              {/* DKIM - Shows Auth rate for forwarders, Alignment rate for direct senders */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 flex items-center gap-1">
                    {isForwarder(source) ? (
                      <>
                        DKIM Auth
                        <span
                          className="text-xs text-slate-400 cursor-help"
                          title="DKIM alignment always fails for forwarders because they modify messages. This shows raw DKIM authentication before forwarding."
                        >
                          <Info className="w-3 h-3" />
                        </span>
                      </>
                    ) : (
                      <>
                        DKIM Aligned
                        <span
                          className="text-xs text-slate-400"
                          title="DKIM passed AND signing domain aligns with From header"
                        >
                          <Info className="w-3 h-3" />
                        </span>
                      </>
                    )}
                  </span>
                  <span
                    className={`font-medium ${
                      parseFloat(
                        calculateRate(aggregatedStats.dkimPass, aggregatedStats.dkimFail)
                      ) >= 80
                        ? "text-emerald-700"
                        : parseFloat(
                            calculateRate(aggregatedStats.dkimPass, aggregatedStats.dkimFail)
                          ) >= 50
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  >
                    {calculateRate(aggregatedStats.dkimPass, aggregatedStats.dkimFail)}%
                  </span>
                </div>
                <div className="flex gap-1 text-xs text-slate-500">
                  <span className="text-emerald-700">
                    {aggregatedStats.dkimPass.toLocaleString()} {isForwarder(source) ? "auth pass" : "aligned"}
                  </span>
                  <span>/</span>
                  <span className="text-red-600">
                    {aggregatedStats.dkimFail.toLocaleString()} {isForwarder(source) ? "auth fail" : "not aligned"}
                  </span>
                </div>
                {isForwarder(source) && (
                  <div className="mt-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    Forwarder - DKIM alignment expected to fail
                  </div>
                )}
              </div>

              {/* DMARC Result */}
              <div className="pt-2 border-t border-slate-100">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 font-medium">DMARC Result</span>
                  <span
                    className={`font-medium ${
                      parseFloat(
                        calculateRate(aggregatedStats.dmarcPass, aggregatedStats.dmarcFail)
                      ) >= 80
                        ? "text-emerald-700"
                        : parseFloat(
                            calculateRate(aggregatedStats.dmarcPass, aggregatedStats.dmarcFail)
                          ) >= 50
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  >
                    {calculateRate(aggregatedStats.dmarcPass, aggregatedStats.dmarcFail)}%
                  </span>
                </div>
                <div className="flex gap-1 text-xs text-slate-500">
                  <span className="text-emerald-700">
                    {aggregatedStats.dmarcPass.toLocaleString()} pass
                  </span>
                  <span>/</span>
                  <span className="text-red-600">
                    {aggregatedStats.dmarcFail.toLocaleString()} fail
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400 text-center py-4">
              No authentication data
            </div>
          )}
        </div>

        {/* Source Details */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-3">
            <Server className="w-4 h-4" />
            <span className="text-sm font-medium">Source Details</span>
          </div>
          <div className="space-y-2 text-sm">
            {/* IP Address */}
            {source.source_ip && (
              <div className="flex justify-between">
                <span className="text-slate-500">IP Address</span>
                <span className="font-mono text-slate-700">{source.source_ip}</span>
              </div>
            )}

            {/* PTR Hostname */}
            {source.ptr_hostname && (
              <div className="flex justify-between">
                <span className="text-slate-500">PTR / Server</span>
                <span className="font-mono text-slate-700 text-right max-w-[200px] truncate">
                  {source.ptr_hostname}
                </span>
              </div>
            )}

            {/* Mail From (Envelope From) */}
            {source.common_envelope_from && (
              <div className="flex justify-between">
                <span className="text-slate-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Mail From
                </span>
                <span className="font-mono text-slate-700 text-right max-w-[200px] truncate">
                  {source.common_envelope_from}
                </span>
              </div>
            )}

            {/* Organization */}
            {source.org_name && (
              <div className="flex justify-between">
                <span className="text-slate-500">Organization</span>
                <span className="text-slate-700">{source.org_name}</span>
              </div>
            )}

            {/* Provider */}
            {source.provider_name && (
              <div className="flex justify-between">
                <span className="text-slate-500">Provider</span>
                <span className="text-slate-700">{source.provider_name}</span>
              </div>
            )}

            {/* DKIM Selectors with per-selector stats */}
            {metadata && metadata.dkim_selector_stats && metadata.dkim_selector_stats.length > 0 ? (
              <div className="border-t border-slate-100 pt-2 mt-2">
                <div className="flex items-center gap-1 text-slate-500 mb-2">
                  <Key className="w-3 h-3" />
                  <span className="text-sm">DKIM Selectors</span>
                </div>
                <div className="space-y-1.5">
                  {metadata.dkim_selector_stats.map((stat) => {
                    const passRate = stat.total > 0 ? (stat.pass / stat.total) * 100 : 0;
                    const barColor = passRate >= 80 ? "bg-emerald-500" : passRate >= 50 ? "bg-amber-500" : "bg-red-600";
                    return (
                      <div key={stat.selector} className="text-xs">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-mono text-slate-600 truncate max-w-[140px]" title={stat.selector}>
                            {stat.selector}
                          </span>
                          <span className={`font-medium ${passRate >= 80 ? "text-emerald-700" : passRate >= 50 ? "text-amber-600" : "text-red-600"}`}>
                            {passRate.toFixed(0)}%
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full ${barColor}`} style={{ width: `${passRate}%` }} />
                          </div>
                          <span className="text-slate-400 min-w-[60px] text-right">
                            {stat.pass.toLocaleString()}/{stat.total.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : metadata && metadata.dkim_selectors && metadata.dkim_selectors.length > 0 ? (
              <div className="flex justify-between">
                <span className="text-slate-500 flex items-center gap-1">
                  <Key className="w-3 h-3" />
                  DKIM Selector{metadata.dkim_selectors.length > 1 ? "s" : ""}
                </span>
                <span className="font-mono text-slate-700 text-right max-w-[200px] truncate" title={metadata.dkim_selectors.join(", ")}>
                  {metadata.dkim_selectors.length <= 2
                    ? metadata.dkim_selectors.join(", ")
                    : `${metadata.dkim_selectors.slice(0, 2).join(", ")} +${metadata.dkim_selectors.length - 2}`
                  }
                </span>
              </div>
            ) : null}

            {/* Envelope From Domains - from metadata (if different from common_envelope_from) */}
            {metadata && metadata.envelope_from_domains && metadata.envelope_from_domains.length > 1 && (
              <div className="flex justify-between">
                <span className="text-slate-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  All Mail From
                </span>
                <span className="font-mono text-slate-700 text-right max-w-[200px] truncate" title={metadata.envelope_from_domains.join(", ")}>
                  {metadata.envelope_from_domains.length <= 2
                    ? metadata.envelope_from_domains.join(", ")
                    : `${metadata.envelope_from_domains.slice(0, 2).join(", ")} +${metadata.envelope_from_domains.length - 2}`
                  }
                </span>
              </div>
            )}

            {/* Forwarder Status */}
            {source.source_type && source.source_type !== "direct" && source.source_type !== "unknown" && (
              <div className="flex justify-between">
                <span className="text-slate-500 flex items-center gap-1">
                  <ArrowRightLeft className="w-3 h-3" />
                  Source Type
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                  {formatSourceType(source.source_type)}
                </span>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-slate-100 my-2" />

            {/* First/Last Seen */}
            <div className="flex items-center gap-1 text-slate-500">
              <Calendar className="w-3 h-3" />
              <span>First seen:</span>
              <span className="text-slate-700">
                {new Date(source.first_seen_at).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-1 text-slate-500">
              <Calendar className="w-3 h-3" />
              <span>Last seen:</span>
              <span className="text-slate-700">
                {new Date(source.last_seen_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Breakdown Table */}
      {metadata?.daily_detail && metadata.daily_detail.length > 0 && (
        <DailyBreakdownTable dailyDetail={metadata.daily_detail} />
      )}
    </div>
  );
}